import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Flex,
  Heading,
  HStack,
  IconButton,
  Image,
  SimpleGrid,
  Stack,
  Text,
  Tag,
  useColorModeValue,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import highlight1 from "../../assets/site-cav_7.png";
import highlight2 from "../../assets/site-cav_2.png";
import highlight3 from "../../assets/site-cav_6.jpg";
import labPicture1 from "../../assets/slider2.jpg";
import labPicture2 from "../../assets/site-cav_4.jpg";
import labPicture3 from "../../assets/site-cav_qlab.png";

const newPhotos = [
  "IMG_6097.jpg", "IMG_5642.jpg", "IMG_5322.jpg", "IMG_5650.jpg", "IMG_6117.jpg",
  "IMG_6056.jpg", "IMG_6316.jpg", "IMG_6315.jpg", "IMG_5210.jpg", "IMG_5611.jpg",
  "IMG_5710.jpg", "IMG_5222.jpg", "IMG_6104.jpg", "IMG_6088.jpg", "IMG_5823.jpg",
  "IMG_5582.jpg", "IMG_5698.jpg", "IMG_6259.jpg", "IMG_6109.jpg",
].map((filename, index) => ({
  src: `/site-cav-media/${filename}`,
  alt: `SITE-CAV lab photo ${index + 1}`,
}));

const labVideos = [
  {
    src: "/site-cav-media/DRAFT%201%20-%20Hackathon.mp4",
    alt: "Hackathon",
    poster: "/site-cav_logo.jpg",
  },
  {
    src: "/site-cav-media/DRAFT%201%20-%20SCIVC%20showcase.mp4",
    alt: "SCVIC Showcase",
    poster: "/site-cav_logo.jpg",
  },
];

function VideoSlide({ video }) {
  const videoRef = useRef(null);
  const [started, setStarted] = useState(false);
  const [playError, setPlayError] = useState(false);

  const play = async () => {
    setStarted(true);
    setPlayError(false);
    try {
      await videoRef.current.play();
      videoRef.current?.focus();
    } catch {
      setPlayError(true);
    }
  };

  return (
    <Box position="relative" w="100%" h="100%" bg="black">
      <Box
        as="video"
        ref={videoRef}
        src={video.src}
        poster={video.poster}
        aria-label={video.alt}
        controls={started}
        controlsList="nodownload"
        playsInline
        preload="none"
        onEnded={() => setStarted(false)}
        onContextMenu={(event) => event.preventDefault()}
        w="100%"
        h="100%"
        objectFit="contain"
      >
        Your browser does not support embedded video playback.
      </Box>
      {!started && (
        <Box
          as="button"
          type="button"
          aria-label={`Play ${video.alt}`}
          position="absolute"
          inset={0}
          w="100%"
          h="100%"
          onClick={play}
          cursor="pointer"
          data-group=""
          _focusVisible={{ outline: "3px solid #DC242C", outlineOffset: "-3px" }}
        >
          <Image src={video.poster} alt="" w="100%" h="100%" objectFit="contain" />
          <Flex
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            align="center"
            justify="center"
            boxSize={{ base: "64px", md: "88px" }}
            borderRadius="full"
            bg="#DC242C"
            border="3px solid white"
            boxShadow="0 8px 30px rgba(0,0,0,0.4)"
            _groupHover={{ bg: "#B91C1C" }}
          >
            <Box as="svg" viewBox="0 0 24 24" boxSize="36px" fill="white" aria-hidden="true">
              <path d="M8 5v14l11-7z" />
            </Box>
          </Flex>
        </Box>
      )}
      {playError && (
        <Text role="status" position="absolute" top={2} left={2} right={2} bg="blackAlpha.800" color="white" p={2}>
          Playback could not start. Please try again.
        </Text>
      )}
    </Box>
  );
}

function InlineCarousel({
  images,
  title,
  subtitle,
  autoPlay = true,
  isVideo = false,
  intervalMs = 4500,
  aspect = { base: "16 / 10", md: "16 / 8" },
}) {
  const [idx, setIdx] = useState(0);
  const border = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.10)");
  const panelBg = useColorModeValue("white", "rgba(255,255,255,0.03)");
  const titleColor = useColorModeValue("#315369", "white");
  const subColor = useColorModeValue("gray.600", "gray.300");
  const dotOff = useColorModeValue("rgba(2,6,23,0.18)", "rgba(255,255,255,0.22)");
  const shadow = useColorModeValue("0 14px 40px rgba(0,0,0,0.10)", "none");
  const mediaBg = useColorModeValue("rgba(49,83,105,0.03)", "rgba(255,255,255,0.03)");
  const arrowBg = useColorModeValue("rgba(255,255,255,0.82)", "rgba(15,23,42,0.65)");
  const n = images?.length || 0;

  useEffect(() => {
    if (isVideo || !autoPlay || n <= 1) return;
    const timer = setInterval(() => setIdx((value) => (value + 1) % n), intervalMs);
    return () => clearInterval(timer);
  }, [autoPlay, intervalMs, isVideo, n]);

  if (!n) return null;
  const current = images[idx];
  const prev = () => setIdx((value) => (value - 1 + n) % n);
  const next = () => setIdx((value) => (value + 1) % n);

  return (
    <Box
      border="1px solid"
      borderColor={border}
      borderRadius="28px"
      overflow="hidden"
      bg={panelBg}
      boxShadow={shadow}
      role="region"
      aria-label={title}
      aria-roledescription="carousel"
    >
      <Box p={{ base: 4, md: 6 }}>
        <Heading fontSize={{ base: "lg", md: "xl" }} color={titleColor} letterSpacing="tight">
          {title}
        </Heading>
        {subtitle && <Text color={subColor} mt={1}>{subtitle}</Text>}
      </Box>
      <Divider borderColor={border} />
      <Box position="relative" p={{ base: 3, md: 4 }}>
        <Box border="1px solid" borderColor={border} borderRadius="22px" bg={mediaBg} overflow="hidden">
          <Box w="100%" sx={{ aspectRatio: aspect }}>
            <Flex w="100%" h="100%" align="center" justify="center" p={{ base: 3, md: 5 }}>
              {isVideo ? (
                <VideoSlide key={current.src} video={current} />
              ) : (
                <Image
                  src={current.src}
                  alt={current.alt || `Slide ${idx + 1}`}
                  w="100%"
                  h="100%"
                  objectFit="contain"
                  draggable={false}
                  loading="lazy"
                  decoding="async"
                />
              )}
            </Flex>
          </Box>
        </Box>
        {n > 1 && (
          <>
            <IconButton
              aria-label={`Previous slide in ${title}`}
              icon={<ChevronLeftIcon />}
              position="absolute"
              left={{ base: 2, md: 4 }}
              top="50%"
              transform="translateY(-50%)"
              onClick={prev}
              variant="ghost"
              bg={arrowBg}
              borderRadius="full"
            />
            <IconButton
              aria-label={`Next slide in ${title}`}
              icon={<ChevronRightIcon />}
              position="absolute"
              right={{ base: 2, md: 4 }}
              top="50%"
              transform="translateY(-50%)"
              onClick={next}
              variant="ghost"
              bg={arrowBg}
              borderRadius="full"
            />
          </>
        )}
        {n > 1 && (
          <HStack justify="center" spacing={2} pt={4}>
            {images.map((_, index) => (
              <Box
                as="button"
                type="button"
                key={index}
                aria-label={`Go to slide ${index + 1} in ${title}`}
                aria-current={index === idx ? "true" : undefined}
                w="8px"
                h="8px"
                borderRadius="full"
                bg={index === idx ? "#DC242C" : dotOff}
                cursor="pointer"
                onClick={() => setIdx(index)}
              />
            ))}
          </HStack>
        )}
        {isVideo && (
          <Text textAlign="center" color={subColor} mt={3} aria-live="polite">
            {current.alt} ({idx + 1} / {n})
          </Text>
        )}
      </Box>
    </Box>
  );
}

function Section({ title, subtitle, children }) {
  const titleColor = useColorModeValue("#315369", "white");
  const subColor = useColorModeValue("gray.600", "gray.300");

  return (
    <Box py={{ base: 10, md: 14 }}>
      <Container maxW="1200px">
        <Stack spacing={2} mb={{ base: 6, md: 8 }}>
          <Heading
            fontSize={{ base: "2xl", md: "3xl" }}
            color={titleColor}
            letterSpacing="tight"
          >
            {title}
          </Heading>
          {subtitle ? (
            <Text fontSize={{ base: "md", md: "lg" }} color={subColor} maxW="900px">
              {subtitle}
            </Text>
          ) : null}
        </Stack>
        {children}
      </Container>
    </Box>
  );
}

function InfoCard({ label, text }) {
  const cardBg = useColorModeValue("white", "rgba(255,255,255,0.04)");
  const border = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.10)");
  const labelColor = useColorModeValue("#315369", "white");
  const bodyColor = useColorModeValue("gray.700", "gray.200");

  return (
    <Box
      bg={cardBg}
      border="1px solid"
      borderColor={border}
      borderRadius="24px"
      p={{ base: 6, md: 7 }}
      boxShadow={useColorModeValue("0 12px 30px rgba(0,0,0,0.08)", "none")}
    >
      <HStack spacing={3} mb={3}>
        <Tag
          size="lg"
          borderRadius="full"
          bg={useColorModeValue("rgba(220,36,44,0.10)", "rgba(220,36,44,0.20)")}
          color="#DC242C"
          fontWeight="900"
          letterSpacing="0.06em"
        >
          {label}
        </Tag>
      </HStack>
      <Text fontSize={{ base: "md", md: "lg" }} color={bodyColor} textAlign="left">
        {text}
      </Text>
    </Box>
  );
}

export default function HomePage() {
  const accent = "#DC242C";
  const heroText = useColorModeValue("#315369", "white");
  const heroSub = useColorModeValue("gray.600", "gray.300");
  const heroBg = useColorModeValue(
    "linear-gradient(135deg, rgba(49,83,105,0.10), rgba(220,36,44,0.10))",
    "linear-gradient(135deg, rgba(49,83,105,0.15), rgba(220,36,44,0.15))"
  );
  const softBorder = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.10)");

  const highlights = useMemo(
    () => [
      ...newPhotos.slice(0, 2),
      { src: highlight1, alt: "SITE-CAV highlight 1" },
      { src: highlight2, alt: "SITE-CAV highlight 2" },
      { src: highlight3, alt: "SITE-CAV highlight 3" },
    ],
    []
  );

  const labPictures = useMemo(
    () => [
      ...newPhotos.slice(2, 5),
      { src: labPicture1, alt: "SITE-CAV lab picture 1" },
      { src: labPicture2, alt: "SITE-CAV lab picture 2" },
      { src: labPicture3, alt: "SITE-CAV lab picture 3" },
    ],
    []
  );

  return (
    <Box>
      {/* HERO */}
      <Box
        pt={{ base: "140px", md: "140px" }}
        pb={{ base: 10, md: 14 }}
        bg={heroBg}
        borderBottom="1px solid"
        borderColor={softBorder}
      >
        <Container maxW="1200px">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 8, md: 12 }} alignItems="center">
            <Stack spacing={5}>
              <HStack spacing={2} flexWrap="wrap">
                <Tag
                  size="lg"
                  borderRadius="full"
                  bg={useColorModeValue("white", "rgba(255,255,255,0.06)")}
                  border="1px solid"
                  borderColor={softBorder}
                  fontWeight="800"
                >
                  University of Ottawa
                </Tag>
                <Tag
                  size="lg"
                  borderRadius="full"
                  bg={useColorModeValue("rgba(220,36,44,0.10)", "rgba(220,36,44,0.20)")}
                  color={accent}
                  fontWeight="900"
                >
                  SITE-CAV
                </Tag>
              </HStack>

              <Heading
                color={heroText}
                fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                lineHeight={1.05}
                letterSpacing="tight"
              >

                SITE-CAV: Secure, Intelligent & Trustworthy Ecosystems for{" "}
                <Box as="span" color={accent}>
                  Connected and Autonomous Vehicles
                </Box>
              </Heading>

              <Text fontSize={{ base: "md", md: "lg" }} color={heroSub} maxW="52ch">
                We advance AI-driven mobility, security-by-design, resilient connectivity, and legally-aware automation
                to enable safer autonomous transportation.
              </Text>

              <HStack spacing={3} flexWrap="wrap">
                <Button
                  as="a"
                  href="/publications"
                  bg={accent}
                  color="white"
                  fontWeight="900"
                  size="lg"
                  borderRadius="16px"
                  _hover={{ filter: "brightness(0.95)" }}
                >
                  Explore Publications
                </Button>
                <Button
                  as="a"
                  href="/seminars"
                  variant="outline"
                  borderColor={softBorder}
                  size="lg"
                  borderRadius="16px"
                  fontWeight="900"
                >
                  Seminars
                </Button>
                <Button
                  as="a"
                  href="/contact-us"
                  variant="ghost"
                  size="lg"
                  borderRadius="16px"
                  fontWeight="900"
                  color={heroText}
                  _hover={{ bg: useColorModeValue("rgba(49,83,105,0.08)", "rgba(255,255,255,0.06)") }}
                >
                  Collaborate →
                </Button>
              </HStack>

              {/* Quick highlights */}
              <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={3} pt={2}>
                {[
                  { k: "Focus", v: "Secure CAV Ecosystems" },
                  { k: "Methods", v: "AI + Networking + Testbeds" },
                  { k: "Impact", v: "Safety, Trust, Policy" },
                ].map((x) => (
                  <Box
                    key={x.k}
                    border="1px solid"
                    borderColor={softBorder}
                    bg={useColorModeValue("rgba(255,255,255,0.65)", "rgba(255,255,255,0.04)")}
                    borderRadius="18px"
                    p={4}
                  >
                    <Text fontSize="xs" letterSpacing="0.12em" fontWeight="900" color={heroSub}>
                      {x.k.toUpperCase()}
                    </Text>
                    <Text fontSize="sm" fontWeight="800" color={heroText} mt={1}>
                      {x.v}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Stack>

            {/* Right visual: FIXED IMAGE SIZING HERE */}
            <Box
              borderRadius="28px"
              overflow="hidden"
              border="1px solid"
              borderColor={softBorder}
              bg={useColorModeValue("white", "rgba(255,255,255,0.03)")}
              boxShadow={useColorModeValue("0 18px 50px rgba(0,0,0,0.12)", "none")}
            >
              <Image
                src="home-img-1.png"
                alt="SITE-CAV"
                w="100%"
                // UPDATED: Changed fixed height to auto and objectFit to contain
                h="auto"
                objectFit="contain"
              />
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* FEATURED CAROUSEL */}
      <Box py={{ base: 10, md: 12 }}>
        <Container maxW="1200px">
          <InlineCarousel
            images={highlights}
            title="Highlights"
            subtitle="Projects, partnerships, and experimental work from the lab."
            intervalMs={4500}
          />
        </Container>
      </Box>

      {/* MISSION / VISION / GOAL */}
      <Section
        title="Mission, Vision & Goal"
        subtitle=""
      >
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
          <InfoCard
            label="Mission"
            text={
              <>
                <b>SITE-CAV</b> aims to enable the seamless integration of connected and autonomous vehicles (CAVs)
                into Ontario’s evolving transportation ecosystem by advancing research in secure, intelligent, and
                legally-aware decision-making systems. Through interdisciplinary collaboration, cutting-edge
                experimental testbeds, and industry partnerships, the project develops resilient communication
                protocols, security-by-design frameworks, and next-generation mobility solutions.
              </>
            }
          />
          <InfoCard
            label="Vision"
            text={
              <>
                The vision of SITE-CAV is to pioneer a secure, efficient, and trustworthy ecosystem for CAVs, where
                intelligent transportation systems adapt to technological advancements and societal needs. By bridging
                AI-driven decision support, ultra-fast connectivity, and legal-aware automation, the project aspires to
                set new global standards in autonomous mobility and smart infrastructure.
              </>
            }
          />
          <InfoCard
            label="Goal"
            text={
              <>
                SITE-CAV&apos;s primary goal is to develop, test, and commercialize innovative solutions that enhance
                the safety, reliability, and efficiency of CAVs within complex urban and intercity environments. By
                leveraging state-of-the-art research, experimental testbeds, and industry-driven insights, the project
                aims to shape policies, optimize supply chains, and strengthen cybersecurity measures for the future of
                autonomous transportation.
              </>
            }
          />
        </SimpleGrid>
      </Section>

      <Divider borderColor={softBorder} />

      <Box py={{ base: 4, md: 6 }}>
        <Container maxW="1200px">
          <InlineCarousel
            images={labVideos}
            title="Lab Videos"
            subtitle="Watch our hackathon and SCVIC showcase."
            isVideo
            autoPlay={false}
            aspect={{ base: "16 / 9", md: "16 / 9" }}
          />
        </Container>
      </Box>

      {/* WHAT WE DO */}
      <Section
        title="What We Do"
        subtitle="Core research themes that define SITE-CAV’s technical direction."
      >
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={5}>
          {[
            {
              t: "Secure-by-Design CAVs",
              d: "Threat modeling, resilience, and safety constraints from day one.",
            },
            {
              t: "AI Decision Support",
              d: "Predictive perception, risk estimation, and trustworthy autonomy.",
            },
            {
              t: "Ultra-Fast Connectivity",
              d: "V2X, edge-cloud, NFV orchestration, and robust networking.",
            },
            {
              t: "Legal-Aware Automation",
              d: "Policy-aligned behaviors and compliance-aware system design.",
            },
          ].map((x) => (
            <Box
              key={x.t}
              border="1px solid"
              borderColor={softBorder}
              borderRadius="22px"
              p={6}
              bg={useColorModeValue("white", "rgba(255,255,255,0.03)")}
              boxShadow={useColorModeValue("0 10px 24px rgba(0,0,0,0.07)", "none")}
            >
              <Text fontWeight="900" color={heroText} fontSize="lg" mb={2}>
                {x.t}
              </Text>
              <Text color={heroSub} fontSize="md">
                {x.d}
              </Text>
            </Box>
          ))}
        </SimpleGrid>
      </Section>

      {/* SECOND CAROUSEL */}
      <Box pb={{ base: 14, md: 16 }}>
        <Container maxW="1200px">
          <InlineCarousel
            images={labPictures}
            title="Lab Pictures"
            subtitle="Demonstrations, testbeds, and research updates."
            intervalMs={5000}
          />
        </Container>
      </Box>

      <Box pb={{ base: 14, md: 16 }}>
        <Container maxW="1200px">
          <Stack spacing={{ base: 8, md: 12 }}>
            <InlineCarousel
              images={newPhotos.slice(5, 12)}
              title="Lab Gallery 1"
              subtitle="SITE-CAV activities, events, and collaborations."
              intervalMs={5000}
            />
            <InlineCarousel
              images={newPhotos.slice(12)}
              title="Lab Gallery 2"
              subtitle="More moments from the SITE-CAV community."
              intervalMs={5000}
            />
          </Stack>
        </Container>
      </Box>

      {/* FINAL CTA */}
      <Box
        py={{ base: 12, md: 16 }}
        bg={useColorModeValue("rgba(49,83,105,0.06)", "rgba(255,255,255,0.03)")}
        borderTop="1px solid"
        borderColor={softBorder}
      >
        <Container maxW="1200px">
          <Flex
            direction={{ base: "column", md: "row" }}
            align={{ base: "stretch", md: "center" }}
            justify="space-between"
          >
            <Stack >
              <Heading fontSize={{ base: "2xl", md: "3xl" }} color={heroText}>
                Want to collaborate?
              </Heading>
              <Text color={heroSub} maxW="70ch">
                We welcome collaborations with academia, industry, and public-sector partners in connected mobility,
                cybersecurity, edge networking, and intelligent transportation systems.
              </Text>
            </Stack>
            <HStack flexWrap="wrap">
              <Button
                as="a"
                href="/contact-us"
                bg={accent}
                color="white"
                fontWeight="900"
                size="lg"
                borderRadius="16px"
                _hover={{ filter: "brightness(0.95)" }}
              >
                Contact the Lab
              </Button>
              <Button
                as="a"
                href="/about-us"
                variant="outline"
                borderColor={softBorder}
                size="lg"
                borderRadius="16px"
                fontWeight="900"
              >
                Meet the Team
              </Button>
            </HStack>
          </Flex>
        </Container>
      </Box>
    </Box>
  );
}
